import { baseApi } from 'core/api/base'
import { IApiClientTagMe } from 'core/interfaces/Me'
import { ITag } from 'core/interfaces/Tag'
import { generatePath } from 'react-router'

/**
 * API RTK Query pour la gestion des tags.
 *
 * Responsabilités :
 * - récupérer les tags du user connecté (avec permissions)
 * - récupérer les tags du client avec leurs applications
 * - gérer les mutations (create / update / delete)
 *
 * Gestion du cache :
 * - séparation volontaire entre :
 *   - Tag (tags du user connecté)
 *   - TagsWithApps (tags globaux du client avec applications)
 *
 * Convention de cache :
 * - update => invalidation fine (id)
 * - create / delete => invalidation de liste (LIST)
 */
const CLIENT_TAGS_ENDPOINT = '/clients/:idClient/tags/me'
const CLIENT_TAGS_CREATE_ENDPOINT = '/clients/:idClient/tags'
const TAGS_ENDPOINT = '/tags'
const TAG_ENDPOINT = `${TAGS_ENDPOINT}/:idTag`
const TAGS_WITH_APPLICATIONS_ENDPOINT =
  '/clients/:idClient/tags-with-applications'

const tagApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Récupère les tags accessibles par l'utilisateur connecté pour un client donné.
     *
     * Utilisé comme source de vérité côté front pour :
     * - permissions
     * - affichage conditionnel
     *
     * Cache :
     * - fournit un tag par id pour permettre une invalidation fine si besoin
     * - fournit un tag "ME_LIST" pour invalider toute la liste facilement
     */
    getMyTagsByClient: builder.query<
      IApiClientTagMe[],
      {
        idClient: number
      }
    >({
      query: ({ idClient }) =>
        generatePath(CLIENT_TAGS_ENDPOINT, {
          idClient: idClient.toString(),
        }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Tag', id }) as const),
              { type: 'Tag', id: 'ME_LIST' },
            ]
          : [{ type: 'Tag', id: 'ME_LIST' }],
    }),

    /**
     * Récupère tous les tags du client avec leurs applications associées.
     *
     * Utilisé principalement pour :
     * - affichage des tags globaux
     * - gestion fonctionnelle (liaison tags / applications)
     *
     * Cache :
     * - fournit un tag par id pour permettre une invalidation ciblée (update)
     * - fournit un tag "LIST" pour invalider toute la liste (create / delete)
     */
    getAllTagsByClientWithApplications: builder.query<
      ITag[],
      { idClient: number }
    >({
      query: ({ idClient }) =>
        generatePath(TAGS_WITH_APPLICATIONS_ENDPOINT, {
          idClient: idClient.toString(),
        }),
      providesTags: (result: ITag[] | undefined) =>
        result
          ? [
              ...result.map(
                ({ id }) => ({ type: 'TagsWithApps', id }) as const
              ),
              { type: 'TagsWithApps', id: 'LIST' },
            ]
          : [{ type: 'TagsWithApps', id: 'LIST' }],
    }),

    /**
     * Crée un nouveau tag pour un client.
     *
     * Cache :
     * - invalide "TagsWithApps:LIST" car un nouvel élément apparaît dans la liste
     * - invalide "Tag:ME_LIST" car les droits utilisateur peuvent évoluer
     */
    createTag: builder.mutation<
      ITag,
      {
        idClient: number
        nomTag: string
      }
    >({
      query: ({ idClient, nomTag }) => ({
        url: generatePath(CLIENT_TAGS_CREATE_ENDPOINT, {
          idClient: idClient.toString(),
        }),
        method: 'POST',
        body: { nomTag: nomTag, actif: true },
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              { type: 'TagsWithApps', id: 'LIST' },
              { type: 'Tag', id: 'ME_LIST' },
            ],
    }),

    /**
     * Met à jour un tag existant (ex: nom).
     *
     * Cache :
     * - invalide uniquement "TagsWithApps:id" pour un refetch ciblé (optimisation)
     * - invalide "Tag:ME_LIST" pour mettre à jour les libellés côté user
     */
    updateTag: builder.mutation<
      ITag,
      {
        nomTag: string
        idTag: number
      }
    >({
      query: ({ nomTag, idTag }) => ({
        url: generatePath(TAG_ENDPOINT, {
          idTag: idTag.toString(),
        }),
        method: 'PATCH',
        body: { nomTag },
      }),
      invalidatesTags: (result, error, { idTag }) =>
        error
          ? []
          : [
              { type: 'TagsWithApps', id: idTag },
              { type: 'Tag', id: 'ME_LIST' },
            ],
    }),

    /**
     * Supprime un tag.
     *
     * Cache :
     * - invalide "TagsWithApps:LIST" car la liste change (élément supprimé)
     * - invalide "Tag:ME_LIST" car les droits utilisateur peuvent être impactés
     */
    deleteTag: builder.mutation<void, number>({
      query(idTag) {
        return {
          url: generatePath(TAG_ENDPOINT, {
            idTag: idTag.toString(),
          }),
          method: 'DELETE',
        }
      },
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              { type: 'TagsWithApps', id: 'LIST' },
              { type: 'Tag', id: 'ME_LIST' },
            ],
    }),
  }),
})

export const {
  useGetMyTagsByClientQuery,
  useGetAllTagsByClientWithApplicationsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} = tagApi

export { tagApi }
